import React, { useState } from 'react';
import { Box, Container, TextField, Button, Typography, Link, CircularProgress, InputAdornment, IconButton, Card } from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { toast } from 'react-toastify';
import api from '../services/api';
import SiteFooter from '../components/SiteFooter';

const MAROON = '#7b0011';
const GOLD = '#c89428';
const CREAM = '#fdf3e4';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }

    try {
      setLoading(true);
      await login(email, password);
      const { user, userRole } = useAuthStore.getState();
      const resolvedRole = userRole || user?.role || 'USER';
      const isAdmin = ['SUPER_ADMIN', 'CONTENT_MODERATOR', 'FINANCE_ADMIN', 'SUPPORT_ADMIN', 'ID_VERIFICATION_ADMIN'].includes(resolvedRole);
      const requestedPathFromState = typeof location.state === 'object' && location.state && typeof (location.state as { from?: unknown }).from === 'string'
        ? (location.state as { from: string }).from
        : null;
      const requestedPath = requestedPathFromState || searchParams.get('returnTo');
      const safeRequestedPath = requestedPath && requestedPath.startsWith('/') && !requestedPath.startsWith('//') && !requestedPath.startsWith('/login')
        ? requestedPath
        : null;
      navigate(safeRequestedPath || (isAdmin ? '/admin' : '/'), { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: CREAM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        flexDirection: 'column',
      }}
    >
      {/* Background Image (Wedding couple) */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/maandvo-hero-banner.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.15,
          zIndex: 0,
        }}
      />

      {/* Floral decorations */}
      <Box
        component="img"
        src="/maandvo-floral-left.png"
        sx={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: '400px',
          opacity: 0.4,
          zIndex: 1,
          display: { xs: 'none', md: 'block' },
        }}
      />
      <Box
        component="img"
        src="/maandvo-floral-right.png"
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          height: '350px',
          opacity: 0.4,
          zIndex: 1,
          display: { xs: 'none', md: 'block' },
        }}
      />

      {/* Content */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, md: 4 }, minHeight: '90vh' }}>
          {/* Left Section - Desktop Only */}
          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            {/* Logo */}
            <Box>
              <Box component="img" src="/maandvo-logo.png" sx={{ height: 80, marginBottom: 2 }} />
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  fontWeight: 800,
                  color: MAROON,
                  marginBottom: 1,
                }}
              >
                MAANDVO
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  color: MAROON,
                  fontWeight: 500,
                  marginBottom: 2,
                }}
              >
                મેટ્રીમોની સર્વિસ
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  color: MAROON,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  marginBottom: 3,
                  lineHeight: 1.6,
                }}
              >
                સારા સંબંધોની સુંદર શરૂઆત<br />વિશ્વાસ, સચ્ચાર અને સમર્પણ સાથે
              </Typography>
            </Box>

            {/* Trust Icons */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>🛡️</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  સુરક્ષિત પ્રોફાઇલ્સ
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>👥</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  વ્યક્તિગત સેવા
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>🔒</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  સુરક્ષિત અને ગોપનીય
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>❤️</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  વિશ્વસનીય
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Section - Login Form */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Card
              sx={{
                width: '100%',
                maxWidth: 420,
                padding: { xs: 3, md: 4 },
                borderRadius: 2,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                backgroundColor: '#fff',
              }}
            >
              {/* Form Title */}
              <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
                <Typography
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: MAROON,
                    fontFamily: "'Georgia', 'Times New Roman', serif",
                    marginBottom: 1,
                  }}
                >
                  Sign In
                </Typography>
                <Typography variant="body2" sx={{ color: '#8a7a6d', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                  Sign in to your account
                </Typography>
              </Box>

              <form onSubmit={handleLogin}>
                {/* Email Field */}
                <TextField
                  fullWidth
                  label="Email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: MAROON }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& fieldset': { borderColor: '#e8d4b0' },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: MAROON },
                    },
                    '& .MuiInputBase-input::placeholder': { color: '#bbb', opacity: 1 },
                  }}
                />

                {/* Password Field */}
                <TextField
                  fullWidth
                  label="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: MAROON }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: MAROON }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& fieldset': { borderColor: '#e8d4b0' },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: MAROON },
                    },
                  }}
                />

                {/* Forgot Password Link */}
                <Box sx={{ textAlign: 'right', marginBottom: 2 }}>
                  <Link
                    onClick={() => navigate('/forgot-password')}
                    sx={{
                      color: MAROON,
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                {/* Login Button */}
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  sx={{
                    backgroundColor: MAROON,
                    color: '#fff9f2',
                    padding: '12px',
                    fontSize: '1rem',
                    fontFamily: "'Georgia', 'Times New Roman', serif",
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginBottom: 2,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: '#5d000d' },
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#fff9f2' }} /> : 'Sign In'}
                </Button>

                {/* Register Link */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#8a7a6d', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                    New to Maandvo?{' '}
                    <Link
                      onClick={() => navigate('/register')}
                      sx={{
                        color: MAROON,
                        fontWeight: 700,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Create an account
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Card>
          </Box>
        </Box>
      </Container>

      <SiteFooter />
    </Box>
  );
};

export default LoginPage;
